import { Badge, Button, Card, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { ClipboardList, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { listForms, type Form, type FormStatus } from '../../lib/forms-api'

const statusVariant: Record<FormStatus, 'success' | 'neutral' | 'gold'> = {
  published: 'success',
  draft: 'gold',
  closed: 'neutral',
}

export function FormsList() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['forms'],
    queryFn: listForms,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Forms"
        description="Build forms and collect public submissions."
        actions={
          <Button asChild>
            <Link to="/app/forms/new">
              <Plus />
              New form
            </Link>
          </Button>
        }
      />

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading forms…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load forms.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="space-y-3 py-20 text-center">
            <ClipboardList className="mx-auto size-8 text-dim" aria-hidden="true" />
            <p className="text-sm text-muted">
              No forms yet. Create your first form to get started.
            </p>
            <Button asChild variant="secondary">
              <Link to="/app/forms/new">
                <Plus />
                New form
              </Link>
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Slug</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Fields</th>
                  <th className="px-5 py-3 font-semibold">Responses</th>
                </tr>
              </thead>
              <tbody>
                {data.map((f: Form) => (
                  <tr
                    key={f.id}
                    className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover"
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/app/forms/${f.id}`}
                        className="font-medium text-fg hover:text-secondary"
                      >
                        {f.name}
                      </Link>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-muted">{f.slug}</td>
                    <td className="px-5 py-3">
                      <Badge variant={statusVariant[f.status]} className="capitalize">
                        {f.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-muted">{f.fieldCount}</td>
                    <td className="px-5 py-3">
                      <Link
                        to={`/app/forms/${f.id}/responses`}
                        className="text-muted hover:text-fg"
                      >
                        {f.submissionCount}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
